import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, CreditCard, Truck, ShieldCheck, Package } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [shippingPreview, setShippingPreview] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(true);
  const [formData, setFormData] = useState({
    shipping_name: '',
    shipping_address: '',
    shipping_city: '',
    shipping_postal_code: '',
    shipping_country: 'Italia',
    shipping_phone: '',
    notes: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Fetch shipping cost preview
  useEffect(() => {
    const fetchShippingPreview = async () => {
      if (items.length === 0) {
        setLoadingShipping(false);
        return;
      }
      
      try {
        const response = await api.post('/orders/shipping-preview', {
          items: items.map(item => ({
            product_id: item.product.id,
            quantity: item.quantity
          }))
        });
        setShippingPreview(response.data);
      } catch (error) {
        console.error('Error fetching shipping preview:', error);
        // Default to free if API fails
        setShippingPreview({ shipping_cost: 0, total: subtotal });
      } finally {
        setLoadingShipping(false);
      }
    };

    fetchShippingPreview();
  }, [items, subtotal]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Calculate total with shipping
  const shippingCost = shippingPreview?.shipping_cost || 0;
  const total = subtotal + shippingCost;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Devi accettare i Termini e la Policy Resi per procedere');
      return;
    }

    setLoading(true);

    try {
      // Per EXPERIENCE: usa valori di default per indirizzo
      const shippingData = shippingPreview?.all_experience 
        ? {
            ...formData,
            shipping_address: formData.shipping_address || "Esperienza - Nessuna spedizione",
            shipping_city: formData.shipping_city || "N/A",
            shipping_postal_code: formData.shipping_postal_code || "00000"
          }
        : formData;

      const response = await api.post('/orders/checkout', {
        items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        shipping: shippingData,
        origin_url: window.location.origin,
        accepted_terms: true,
        accepted_resi: true,
      });

      if (response.data.checkout_url) {
        // Show toast before redirect
        toast.success('Reindirizzamento a Stripe...');
        
        // Small delay to ensure toast is shown, then redirect
        setTimeout(() => {
          window.location.assign(response.data.checkout_url);
        }, 500);
      } else {
        toast.error('Errore nella creazione del checkout');
        setLoading(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante il checkout';
      toast.error(message);
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/carrello');
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-8">Checkout</h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Shipping Form / Contact Info */}
          <div>
            <div className="bg-white p-6 rounded-xl border border-border">
              <h2 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                {shippingPreview?.all_experience ? (
                  <>
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Dati di Contatto
                  </>
                ) : (
                  <>
                    <Truck className="w-5 h-5 text-primary" />
                    Indirizzo di Spedizione
                  </>
                )}
              </h2>
              
              {shippingPreview?.all_experience && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-purple-700">
                    Stai acquistando un'esperienza. Inserisci i tuoi dati di contatto per ricevere le informazioni sull'evento.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="shipping_name">Nome Completo *</Label>
                  <Input
                    id="shipping_name"
                    value={formData.shipping_name}
                    onChange={(e) => setFormData({ ...formData, shipping_name: e.target.value })}
                    placeholder="Mario Rossi"
                    required
                    className="mt-1"
                    data-testid="shipping-name"
                  />
                </div>

                {/* Per EXPERIENCE: campi opzionali per indirizzo */}
                {shippingPreview?.all_experience ? (
                  <>
                    <div>
                      <Label htmlFor="shipping_phone">Telefono *</Label>
                      <Input
                        id="shipping_phone"
                        value={formData.shipping_phone}
                        onChange={(e) => setFormData({ ...formData, shipping_phone: e.target.value })}
                        placeholder="+39 333 1234567"
                        required
                        className="mt-1"
                        data-testid="shipping-phone"
                      />
                      <p className="text-xs text-text-muted mt-1">
                        Ti contatteremo per i dettagli dell'esperienza
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="notes">Note (opzionale)</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Richieste particolari, allergie alimentari, etc..."
                        className="mt-1"
                        data-testid="notes"
                      />
                    </div>
                    {/* Hidden fields with default values for EXPERIENCE */}
                    <input type="hidden" value={formData.shipping_address || "Esperienza - No spedizione"} />
                    <input type="hidden" value={formData.shipping_city || "N/A"} />
                    <input type="hidden" value={formData.shipping_postal_code || "00000"} />
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="shipping_address">Indirizzo *</Label>
                      <Input
                        id="shipping_address"
                        value={formData.shipping_address}
                        onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                        placeholder="Via Roma 123"
                        required
                        className="mt-1"
                        data-testid="shipping-address"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="shipping_city">Città *</Label>
                        <Input
                          id="shipping_city"
                          value={formData.shipping_city}
                          onChange={(e) => setFormData({ ...formData, shipping_city: e.target.value })}
                          placeholder="Napoli"
                          required
                          className="mt-1"
                          data-testid="shipping-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="shipping_postal_code">CAP *</Label>
                        <Input
                          id="shipping_postal_code"
                          value={formData.shipping_postal_code}
                          onChange={(e) => setFormData({ ...formData, shipping_postal_code: e.target.value })}
                          placeholder="80100"
                          required
                          className="mt-1"
                          data-testid="shipping-postal"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes">Note (opzionale)</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Istruzioni per la consegna..."
                        className="mt-1"
                        rows={3}
                        data-testid="shipping-notes"
                      />
                    </div>

                    <div>
                      <Label htmlFor="shipping_phone">Telefono (opzionale)</Label>
                      <Input
                        id="shipping_phone"
                        type="tel"
                        value={formData.shipping_phone}
                        onChange={(e) => setFormData({ ...formData, shipping_phone: e.target.value })}
                        placeholder="+39 333 1234567"
                        className="mt-1"
                        data-testid="shipping-phone"
                      />
                    </div>
                  </>
                )}

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 flex-shrink-0"
                    checked={acceptedTerms}
                    onChange={e => setAcceptedTerms(e.target.checked)}
                    data-testid="checkout-accept-terms"
                  />
                  <span className="text-sm text-text-secondary">
                    Accetto i{' '}
                    <Link to="/legal/termini-condizioni" target="_blank" className="text-primary underline">
                      Termini
                    </Link>
                    {' '}e la{' '}
                    <Link to="/legal/resi-rimborsi" target="_blank" className="text-primary underline">
                      Policy Resi
                    </Link>
                    {' '}*
                  </span>
                </label>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || loadingShipping || !acceptedTerms}
                  data-testid="pay-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Procedi al Pagamento - {formatPrice(total)}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Trust badges */}
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-text-secondary">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-secondary" />
                <span>Pagamento Sicuro</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-secondary" />
                <span>Stripe</span>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white p-6 rounded-xl border border-border sticky top-24">
              <h2 className="font-heading text-xl font-bold mb-6">Riepilogo Ordine</h2>

              <div className="space-y-4 mb-6">
                {items.map(({ product, quantity }) => (
                  <div key={product.id} className="flex gap-4">
                    <img
                      src={product.image_url || 'https://images.pexels.com/photos/7663192/pexels-photo-7663192.jpeg'}
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{product.name}</h4>
                      <p className="text-sm text-text-secondary">Quantità: {quantity}</p>
                    </div>
                    <p className="font-medium">{formatPrice(product.price * quantity)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Subtotale</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                
                {/* Shipping costs - show per producer if multiple */}
                {loadingShipping ? (
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Spedizione</span>
                    <span className="text-text-muted">Calcolo...</span>
                  </div>
                ) : shippingPreview?.all_experience ? (
                  // All items are EXPERIENCE - no shipping
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-purple-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      <span className="font-medium">Esperienza</span>
                    </div>
                    <p className="text-sm text-purple-600 mt-1">
                      Nessuna spedizione fisica richiesta. Riceverai i dettagli dell'evento via email.
                    </p>
                  </div>
                ) : shippingPreview?.shipping_by_producer?.length > 1 ? (
                  // Multi-vendor: show breakdown per producer
                  <div className="space-y-2">
                    <p className="text-xs text-text-muted font-medium">Spedizione per venditore:</p>
                    {shippingPreview.shipping_by_producer.map((sp) => (
                      <div key={sp.producer_id} className="flex justify-between text-sm pl-2 border-l-2 border-muted">
                        <span className="text-text-secondary truncate max-w-[150px]" title={sp.producer_name}>
                          {sp.producer_name}
                        </span>
                        {sp.shipping_cost === 0 ? (
                          <span className="text-secondary font-medium">Gratis</span>
                        ) : (
                          <span className="font-medium">{formatPrice(sp.shipping_cost)}</span>
                        )}
                      </div>
                    ))}
                    <div className="flex justify-between text-sm font-medium pt-1 border-t border-dashed border-muted">
                      <span className="text-text-secondary">Totale spedizione</span>
                      {shippingCost === 0 ? (
                        <span className="text-secondary">Gratuita</span>
                      ) : (
                        <span>{formatPrice(shippingCost)}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  // Single vendor
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Spedizione</span>
                    {shippingCost === 0 ? (
                      <span className="text-secondary font-medium">Gratuita</span>
                    ) : (
                      <span className="font-medium">{formatPrice(shippingCost)}</span>
                    )}
                  </div>
                )}
                
                {/* Shipping info message */}
                {shippingPreview?.shipping_info && (
                  <div className="text-xs text-text-muted bg-muted/50 p-2 rounded">
                    {shippingPreview.shipping_info}
                  </div>
                )}
                
                {/* Per-producer shipping hints */}
                {shippingPreview?.shipping_by_producer?.filter(sp => sp.shipping_info && sp.shipping_cost > 0).map((sp) => (
                  <div key={`hint-${sp.producer_id}`} className="text-xs text-primary/80 bg-primary/5 p-2 rounded">
                    <strong>{sp.producer_name}:</strong> {sp.shipping_info}
                  </div>
                ))}
                
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
                  <span>Totale</span>
                  <span className="text-primary">{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
